import { Box } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import type * as THREEType from 'three';
import type { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import type { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import type { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { useArtistTheme } from '../../contexts/ArtistThemeContext';

const CLOUD_ATLAS_URL = new URL('../../assets/clouds/fx_cloudalpha05.png', import.meta.url).href;

type CinematicSkyProps = Omit<React.ComponentProps<typeof Box>, 'ref' | 'sx'> & {
  sx?: SxProps<Theme>;
};

type PointerState = {
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  energy: number;
  lastX: number;
  lastY: number;
  lastTime: number;
};

type ThemeColors = {
  background: THREEType.Color;
  accent: THREEType.Color;
  accent2: THREEType.Color;
  text: THREEType.Color;
  cloudLight: THREEType.Color;
  cloudMid: THREEType.Color;
  cloudShade: THREEType.Color;
  sunCore: THREEType.Color;
  sunRim: THREEType.Color;
  moonLight: THREEType.Color;
  moonShade: THREEType.Color;
  isDark: boolean;
};

type CloudSystem = {
  group: THREEType.Group;
  material: THREEType.ShaderMaterial;
  baseX: number;
  baseY: number;
  depth: number;
  parallax: number;
  drift: number;
  radius: number;
  windSpeed: number;
  windPhase: number;
  verticalDrift: number;
};

type CelestialSystem = {
  sunGroup: THREEType.Group;
  sunMaterial: THREEType.ShaderMaterial;
  moon: THREEType.Mesh;
  flareGhostGroup: THREEType.Group;
  flareGhosts: THREEType.Sprite[];
  flareStreaks: THREEType.Sprite[];
  mistMaterial: THREEType.ShaderMaterial;
  veilMaterial: THREEType.ShaderMaterial;
};

type SceneHandle = {
  scene: THREEType.Scene;
  camera: THREEType.PerspectiveCamera;
  renderer: THREEType.WebGLRenderer;
  composer: EffectComposer;
  bloomPass: UnrealBloomPass | null;
  atmospherePass: ShaderPass | null;
  animationId: number;
  handleResize: () => void;
  removeInputListeners: () => void;
  geometries: THREEType.BufferGeometry[];
  materials: THREEType.Material[];
  textures: THREEType.Texture[];
  disposables: Array<{ dispose: () => void }>;
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const mix = (start: number, end: number, amount: number) => start + (end - start) * amount;

const registerMaterial = <T extends THREEType.Material>(material: T, materials: THREEType.Material[]) => {
  materials.push(material);
  return material;
};

const registerGeometry = <T extends THREEType.BufferGeometry>(geometry: T, geometries: THREEType.BufferGeometry[]) => {
  geometries.push(geometry);
  return geometry;
};

const getLuminance = (color: THREEType.Color) => {
  const channel = (value: number) => {
    if (value <= 0.03928) return value / 12.92;
    return Math.pow((value + 0.055) / 1.055, 2.4);
  };

  return 0.2126 * channel(color.r) + 0.7152 * channel(color.g) + 0.0722 * channel(color.b);
};

const readCssColor = (
  THREE: typeof THREEType,
  name: string,
  fallback: string,
  themeStyle?: Record<string, string>
) => {
  const raw = themeStyle?.[name] || getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  try {
    return new THREE.Color(raw || fallback);
  } catch {
    return new THREE.Color(fallback);
  }
};

const offsetColor = (THREE: typeof THREEType, color: THREEType.Color, hue = 0, saturation = 0, lightness = 0) =>
  new THREE.Color(color.getHex()).offsetHSL(hue, saturation, lightness);

const blendColor = (THREE: typeof THREEType, left: THREEType.Color, right: THREEType.Color, amount: number) =>
  new THREE.Color(left.getHex()).lerp(right, clamp(amount, 0, 1));

const colorFromHsl = (THREE: typeof THREEType, hue: number, saturation: number, lightness: number) =>
  new THREE.Color().setHSL(
    ((hue % 1) + 1) % 1,
    clamp(saturation, 0, 1),
    clamp(lightness, 0, 1),
    THREE.SRGBColorSpace
  );

const getPerceivedBrightness = (color: THREEType.Color) => {
  const srgb = color.clone().convertLinearToSRGB();
  return 0.299 * srgb.r + 0.587 * srgb.g + 0.114 * srgb.b;
};

const getHsl = (color: THREEType.Color) => {
  const hsl = { h: 0, s: 0, l: 0 };
  color.clone().convertLinearToSRGB().getHSL(hsl);
  return hsl;
};

const getBackgroundSkyPalette = (THREE: typeof THREEType, background: THREEType.Color, isDark: boolean) => {
  const hsl = getHsl(background);
  const hasHue = hsl.s > 0.08;

  if (!hasHue) {
    return {
      accent: new THREE.Color('#d8b375'),
      accent2: new THREE.Color('#fff0d0'),
      cloudLight: new THREE.Color('#f4dfbd'),
      cloudMid: new THREE.Color('#b99663'),
      cloudShade: new THREE.Color('#4b3c2a'),
      sunCore: new THREE.Color('#fff4ba'),
      sunRim: new THREE.Color('#f2a33a'),
      moonLight: new THREE.Color('#f6ead8'),
      moonShade: new THREE.Color('#6d604d'),
    };
  }

  const saturation = clamp(Math.max(hsl.s, 0.36) + 0.1, 0.38, 0.82);
  const lightBase = isDark ? 0.62 : clamp(hsl.l + 0.18, 0.68, 0.82);
  const highLight = isDark ? 0.82 : clamp(hsl.l + 0.38, 0.88, 0.96);
  const shadeLight = isDark ? 0.22 : clamp(hsl.l + 0.08, 0.6, 0.74);

  return {
    accent: colorFromHsl(THREE, hsl.h + (isDark ? -0.055 : 0.025), saturation, lightBase),
    accent2: colorFromHsl(THREE, hsl.h + (isDark ? 0.035 : 0.095), saturation * 0.72, highLight),
    cloudLight: colorFromHsl(THREE, hsl.h + (isDark ? 0.02 : 0.065), saturation * 0.46, highLight),
    cloudMid: colorFromHsl(THREE, hsl.h + (isDark ? -0.015 : 0.025), saturation * 0.54, isDark ? 0.54 : clamp(hsl.l + 0.16, 0.66, 0.84)),
    cloudShade: colorFromHsl(THREE, hsl.h + (isDark ? -0.035 : 0.005), saturation * (isDark ? 0.38 : 0.28), shadeLight),
    sunCore: colorFromHsl(THREE, hsl.h + (isDark ? -0.08 : 0.08), saturation * 0.52, isDark ? 0.88 : 0.93),
    sunRim: colorFromHsl(THREE, hsl.h + (isDark ? -0.12 : 0.035), saturation * 0.66, isDark ? 0.66 : 0.76),
    moonLight: colorFromHsl(THREE, hsl.h + 0.04, saturation * 0.26, isDark ? 0.76 : 0.7),
    moonShade: colorFromHsl(THREE, hsl.h + (isDark ? -0.025 : 0.01), saturation * 0.3, isDark ? 0.28 : 0.42),
  };
};

const liftColorForSky = (
  THREE: typeof THREEType,
  color: THREEType.Color,
  fallback: THREEType.Color,
  minLuminance: number
) => {
  const current = new THREE.Color(color.getHex());
  const luminance = getLuminance(current);
  if (luminance >= minLuminance) return current;

  const liftAmount = clamp((minLuminance - luminance) / Math.max(minLuminance, 0.001), 0.34, 0.86);
  const lifted = blendColor(THREE, current, fallback, liftAmount);
  if (getLuminance(lifted) >= minLuminance * 0.86) return lifted;

  return blendColor(THREE, lifted, new THREE.Color('#fff2c4'), 0.42);
};

const getThemeColors = (THREE: typeof THREEType, themeStyle?: Record<string, string>): ThemeColors => {
  const background = readCssColor(THREE, '--backgroundColor', '#0a0a0a', themeStyle);
  const accent = readCssColor(THREE, '--logoColor', '#c9a875', themeStyle);
  const accent2 = readCssColor(THREE, '--logoColor2', '#f2d9aa', themeStyle);
  const text = readCssColor(THREE, '--textColor', '#f7efe0', themeStyle);
  const isDark = getPerceivedBrightness(background) < 0.42;
  const backgroundPalette = getBackgroundSkyPalette(THREE, background, isDark);
  const warmFallback = new THREE.Color('#e8c47f');
  const softFallback = new THREE.Color('#fff0d0');
  const hasUnusableLightThemeAccents =
    !isDark && getLuminance(accent) < 0.08 && getLuminance(accent2) < 0.08 && getLuminance(text) < 0.12;
  const accentFallback = isDark ? warmFallback : backgroundPalette.accent;
  const accent2Fallback = isDark ? softFallback : backgroundPalette.accent2;
  const visibleAccent = hasUnusableLightThemeAccents
    ? backgroundPalette.accent
    : liftColorForSky(THREE, accent, accentFallback, isDark ? 0.26 : 0.36);
  const visibleAccent2 = hasUnusableLightThemeAccents
    ? backgroundPalette.accent2
    : liftColorForSky(THREE, accent2, accent2Fallback, isDark ? 0.38 : 0.46);
  const visibleText = liftColorForSky(THREE, text, accent2Fallback, isDark ? 0.44 : 0.32);
  const lightCloudColor = hasUnusableLightThemeAccents ? backgroundPalette.cloudLight : visibleText;
  const midCloudColor = hasUnusableLightThemeAccents ? backgroundPalette.cloudMid : visibleAccent;
  const shadeCloudColor = hasUnusableLightThemeAccents ? backgroundPalette.cloudShade : background;
  const sunCoreBase = hasUnusableLightThemeAccents ? backgroundPalette.sunCore : new THREE.Color('#fff4ba');
  const sunRimBase = hasUnusableLightThemeAccents ? backgroundPalette.sunRim : new THREE.Color('#f2a33a');

  return {
    background,
    accent: visibleAccent,
    accent2: visibleAccent2,
    text: visibleText,
    cloudLight: isDark
      ? blendColor(THREE, visibleAccent2, visibleText, 0.62)
      : blendColor(THREE, lightCloudColor, visibleAccent2, hasUnusableLightThemeAccents ? 0.34 : 0.26),
    cloudMid: isDark
      ? blendColor(THREE, visibleAccent, visibleText, 0.28)
      : blendColor(THREE, midCloudColor, visibleText, hasUnusableLightThemeAccents ? 0.18 : 0.66),
    cloudShade: isDark
      ? blendColor(THREE, background, visibleAccent, 0.36)
      : blendColor(THREE, shadeCloudColor, visibleAccent, hasUnusableLightThemeAccents ? 0.26 : 0.34),
    sunCore: liftColorForSky(THREE, blendColor(THREE, visibleAccent2, sunCoreBase, 0.62), accent2Fallback, 0.54),
    sunRim: liftColorForSky(THREE, blendColor(THREE, visibleAccent, sunRimBase, 0.48), accentFallback, 0.32),
    moonLight: hasUnusableLightThemeAccents
      ? blendColor(THREE, backgroundPalette.moonLight, visibleAccent2, 0.18)
      : blendColor(THREE, visibleText, visibleAccent2, 0.34),
    moonShade: hasUnusableLightThemeAccents
      ? blendColor(THREE, backgroundPalette.moonShade, background, 0.22)
      : blendColor(THREE, background, visibleAccent, 0.26),
    isDark,
  };
};

const colorStyle = (color: THREEType.Color, alpha: number) =>
  `rgba(${Math.round(color.r * 255)}, ${Math.round(color.g * 255)}, ${Math.round(color.b * 255)}, ${alpha})`;

const makeRadialTexture = (THREE: typeof THREEType, stops: Array<[number, string]>, size = 256) => {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  if (!context) return null;

  const center = size / 2;
  const gradient = context.createRadialGradient(center, center, 0, center, center, center);
  stops.forEach(([offset, color]) => gradient.addColorStop(offset, color));
  context.fillStyle = gradient;
  context.fillRect(0, 0, size, size);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
};

const makeLinearFlareTexture = (THREE: typeof THREEType, theme: ThemeColors, size = 512) => {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size / 4;
  const context = canvas.getContext('2d');
  if (!context) return null;

  const centerY = canvas.height / 2;
  const horizontal = context.createLinearGradient(0, centerY, canvas.width, centerY);
  horizontal.addColorStop(0, colorStyle(theme.sunRim, 0));
  horizontal.addColorStop(0.18, colorStyle(theme.accent2, 0.1));
  horizontal.addColorStop(0.42, colorStyle(theme.sunRim, 0.38));
  horizontal.addColorStop(0.5, colorStyle(theme.sunCore, 0.92));
  horizontal.addColorStop(0.58, colorStyle(theme.sunRim, 0.38));
  horizontal.addColorStop(0.82, colorStyle(theme.accent2, 0.1));
  horizontal.addColorStop(1, colorStyle(theme.sunRim, 0));

  const vertical = context.createLinearGradient(0, 0, 0, canvas.height);
  vertical.addColorStop(0, 'rgba(255,255,255,0)');
  vertical.addColorStop(0.36, 'rgba(255,255,255,0.28)');
  vertical.addColorStop(0.5, 'rgba(255,255,255,1)');
  vertical.addColorStop(0.64, 'rgba(255,255,255,0.28)');
  vertical.addColorStop(1, 'rgba(255,255,255,0)');

  context.fillStyle = horizontal;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.globalCompositeOperation = 'destination-in';
  context.fillStyle = vertical;
  context.fillRect(0, 0, canvas.width, canvas.height);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
};

const makeMoonTexture = (THREE: typeof THREEType, theme: ThemeColors) => {
  const random = (() => {
    let value = 9901;
    return () => {
      value += 0x6d2b79f5;
      let next = value;
      next = Math.imul(next ^ (next >>> 15), next | 1);
      next ^= next + Math.imul(next ^ (next >>> 7), next | 61);
      return ((next ^ (next >>> 14)) >>> 0) / 4294967296;
    };
  })();
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 512;
  const context = canvas.getContext('2d');
  if (!context) return null;

  const center = canvas.width / 2;
  const base = context.createRadialGradient(center - 120, center - 150, 20, center, center, 275);
  base.addColorStop(0, colorStyle(theme.moonLight, 1));
  base.addColorStop(0.62, colorStyle(blendColor(THREE, theme.moonLight, theme.moonShade, 0.34), 1));
  base.addColorStop(1, colorStyle(theme.moonShade, 1));
  context.fillStyle = base;
  context.fillRect(0, 0, canvas.width, canvas.height);

  for (let i = 0; i < 92; i++) {
    const angle = random() * Math.PI * 2;
    const radius = Math.sqrt(random()) * 210;
    const x = center + Math.cos(angle) * radius;
    const y = center + Math.sin(angle) * radius;
    const crater = mix(4, i % 8 === 0 ? 28 : 14, random());
    const shade = context.createRadialGradient(x - crater * 0.2, y - crater * 0.25, crater * 0.15, x, y, crater);
    shade.addColorStop(0, colorStyle(theme.moonLight, mix(0.08, 0.2, random())));
    shade.addColorStop(0.52, colorStyle(theme.moonShade, mix(0.08, 0.22, random())));
    shade.addColorStop(1, colorStyle(theme.moonShade, 0));
    context.fillStyle = shade;
    context.beginPath();
    context.arc(x, y, crater, 0, Math.PI * 2);
    context.fill();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
};

const makeSeededRandom = (seed: number) => {
  let value = seed >>> 0;
  return () => {
    value += 0x6d2b79f5;
    let next = value;
    next = Math.imul(next ^ (next >>> 15), next | 1);
    next ^= next + Math.imul(next ^ (next >>> 7), next | 61);
    return ((next ^ (next >>> 14)) >>> 0) / 4294967296;
  };
};

const makeProceduralCloudTexture = (THREE: typeof THREEType, seed: number) => {
  const random = makeSeededRandom(seed);
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = 384;
  const context = canvas.getContext('2d');
  if (!context) return null;

  const drawBlob = (x: number, y: number, radiusX: number, radiusY: number, alpha: number) => {
    context.save();
    context.translate(x, y);
    context.scale(radiusX, radiusY);
    const gradient = context.createRadialGradient(0, 0, 0, 0, 0, 1);
    gradient.addColorStop(0, `rgba(255,255,255,${alpha})`);
    gradient.addColorStop(0.42, `rgba(255,255,255,${alpha * 0.72})`);
    gradient.addColorStop(0.78, `rgba(255,255,255,${alpha * 0.2})`);
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    context.fillStyle = gradient;
    context.beginPath();
    context.arc(0, 0, 1, 0, Math.PI * 2);
    context.fill();
    context.restore();
  };

  context.clearRect(0, 0, canvas.width, canvas.height);
  context.globalCompositeOperation = 'source-over';
  context.filter = 'blur(18px)';
  drawBlob(canvas.width * 0.5, canvas.height * 0.62, canvas.width * 0.28, canvas.height * 0.13, 0.24);

  context.filter = 'blur(10px)';
  for (let i = 0; i < 15; i += 1) {
    const angle = random() * Math.PI * 2;
    const distance = Math.pow(random(), 0.74);
    const x = canvas.width * 0.5 + Math.cos(angle) * canvas.width * 0.28 * distance;
    const y = canvas.height * 0.5 + Math.sin(angle) * canvas.height * 0.18 * distance - random() * canvas.height * 0.12;
    const radiusX = mix(58, 146, random()) * (0.9 + (1 - distance) * 0.18);
    const radiusY = mix(52, 124, random()) * (0.88 + (1 - distance) * 0.24);
    drawBlob(x, y, radiusX, radiusY, mix(0.24, 0.52, random()));
  }

  context.filter = 'blur(5px)';
  for (let i = 0; i < 24; i += 1) {
    const x = canvas.width * 0.5 + (random() - 0.5) * canvas.width * 0.6;
    const y = canvas.height * mix(0.26, 0.64, random());
    drawBlob(x, y, mix(22, 58, random()), mix(16, 38, random()), mix(0.08, 0.2, random()));
  }

  context.globalCompositeOperation = 'destination-out';
  context.filter = 'blur(16px)';
  for (let i = 0; i < 12; i += 1) {
    drawBlob(
      canvas.width * 0.5 + (random() - 0.5) * canvas.width * 0.54,
      canvas.height * mix(0.34, 0.68, random()),
      mix(34, 96, random()),
      mix(18, 54, random()),
      mix(0.08, 0.18, random())
    );
  }

  context.filter = 'none';
  const image = context.getImageData(0, 0, canvas.width, canvas.height);
  for (let i = 0; i < image.data.length; i += 4) {
    const alpha = image.data[i + 3] / 255;
    const noise = 0.88 + random() * 0.18;
    const thresholdedAlpha = alpha < 0.34 ? 0 : Math.pow((alpha - 0.34) / 0.66, 0.82);
    const nextAlpha = Math.round(clamp(thresholdedAlpha * noise, 0, 1) * 255);
    image.data[i] = 255;
    image.data[i + 1] = 255;
    image.data[i + 2] = 255;
    image.data[i + 3] = nextAlpha;
  }
  context.putImageData(image, 0, 0);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = true;
  return texture;
};

const loadCloudAtlas = (THREE: typeof THREEType) =>
  new Promise<THREEType.Texture>((resolve, reject) => {
    new THREE.TextureLoader().load(
      CLOUD_ATLAS_URL,
      (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;
        texture.minFilter = THREE.LinearMipmapLinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.generateMipmaps = true;
        resolve(texture);
      },
      undefined,
      reject
    );
  });

const makeAtmospherePass = (THREE: typeof THREEType, ShaderPassCtor: typeof ShaderPass, theme: ThemeColors) =>
  new ShaderPassCtor({
    uniforms: {
      tDiffuse: { value: null },
      uTime: { value: 0 },
      uPointer: { value: new THREE.Vector2(0, 0) },
      uTint: { value: theme.accent2 },
      uStrength: { value: theme.isDark ? 0.1 : 0.07 },
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform sampler2D tDiffuse;
      uniform float uTime;
      uniform vec2 uPointer;
      uniform vec3 uTint;
      uniform float uStrength;
      varying vec2 vUv;

      float hash(vec2 p) {
        return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
      }

      void main() {
        vec2 aberration = (vUv - 0.5) * (0.00035 + length(uPointer) * 0.0006);
        vec4 base = texture2D(tDiffuse, vUv);
        vec4 red = texture2D(tDiffuse, vUv + aberration);
        vec4 blue = texture2D(tDiffuse, vUv - aberration);
        vec3 color = vec3(red.r, base.g, blue.b);
        float grain = hash(vUv * vec2(960.0, 540.0) + uTime * 0.35) - 0.5;
        float vignette = smoothstep(0.92, 0.18, distance(vUv, vec2(0.5)));
        color += uTint * grain * uStrength * base.a;
        color *= mix(0.96, 1.025, vignette);
        gl_FragColor = vec4(color, base.a);
      }
    `,
  });

const makeSunMistMaterial = (
  THREE: typeof THREEType,
  theme: ThemeColors,
  materials: THREEType.Material[]
) =>
  registerMaterial(
    new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uPointerEnergy: { value: 0 },
        uSunColor: { value: theme.sunCore },
        uMistColor: { value: blendColor(THREE, theme.accent, theme.text, theme.isDark ? 0.34 : 0.58) },
        uOpacity: { value: theme.isDark ? 0.26 : 0.2 },
        uWind: { value: new THREE.Vector2(0.34, -0.06) },
        uFlow: { value: new THREE.Vector2(0, 0) },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float uTime;
        uniform float uPointerEnergy;
        uniform vec3 uSunColor;
        uniform vec3 uMistColor;
        uniform float uOpacity;
        uniform vec2 uWind;
        uniform vec2 uFlow;
        varying vec2 vUv;

        float hash(vec2 p) {
          return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
        }

        float noise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          vec2 u = f * f * (3.0 - 2.0 * f);
          return mix(
            mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
            mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
            u.y
          );
        }

        float fbm(vec2 p) {
          float value = 0.0;
          float amplitude = 0.5;
          for (int i = 0; i < 4; i++) {
            value += noise(p) * amplitude;
            p = p * 2.03 + vec2(13.4, 7.1);
            amplitude *= 0.5;
          }
          return value;
        }

        void main() {
          vec2 centered = vUv - 0.5;
          float radial = smoothstep(0.88, 0.08, length(centered * vec2(1.08, 1.42)));
          vec2 wind = normalize(uWind + vec2(0.001, 0.001));
          float vapor = fbm(vUv * vec2(3.8, 2.4) + uFlow + wind * uTime * 0.028);
          float rayLine = abs(centered.y * 2.2 + centered.x * 0.34 + sin(uTime * 0.34 + centered.x * 5.0 + uFlow.x * 2.0) * 0.028);
          float ray = pow(1.0 - smoothstep(0.02, 0.52, rayLine), 2.2);
          float pulse = 0.9 + sin(uTime * 1.4) * 0.08 + uPointerEnergy * 0.16;
          float alpha = (vapor * 0.22 + ray * 0.34) * radial * uOpacity * pulse;
          vec3 color = mix(uMistColor, uSunColor, clamp(ray * 0.78 + radial * 0.24, 0.0, 1.0));
          if (alpha < 0.01) discard;
          gl_FragColor = vec4(color, alpha);
        }
      `,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    }),
    materials
  );

const makeSunVeilMaterial = (
  THREE: typeof THREEType,
  cloudAtlas: THREEType.Texture,
  theme: ThemeColors,
  materials: THREEType.Material[]
) =>
  registerMaterial(
    new THREE.ShaderMaterial({
      uniforms: {
        uCloudMap: { value: cloudAtlas },
        uAtlasRect: { value: new THREE.Vector4(0.04, 0.34, 0.88, 0.28) },
        uTime: { value: 0 },
        uPointerEnergy: { value: 0 },
        uSunColor: { value: theme.sunCore },
        uRimColor: { value: theme.sunRim },
        uShadeColor: { value: theme.cloudShade },
        uOpacity: { value: theme.isDark ? 0.28 : 0.1 },
        uWind: { value: new THREE.Vector2(0.34, -0.06) },
        uFlow: { value: new THREE.Vector2(0, 0) },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          vec3 transformed = position;
          transformed.x += sin(uv.y * 8.0) * 0.24;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);
        }
      `,
      fragmentShader: `
        uniform float uTime;
        uniform float uPointerEnergy;
        uniform vec3 uSunColor;
        uniform vec3 uRimColor;
        uniform vec3 uShadeColor;
        uniform float uOpacity;
        uniform vec2 uWind;
        uniform vec2 uFlow;
        varying vec2 vUv;

        float hash(vec2 p) {
          return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
        }

        float noise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          vec2 u = f * f * (3.0 - 2.0 * f);
          return mix(
            mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
            mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
            u.y
          );
        }

        float fbm(vec2 p) {
          float value = 0.0;
          float amplitude = 0.5;
          for (int i = 0; i < 4; i++) {
            value += noise(p) * amplitude;
            p = p * 2.03 + vec2(11.7, 5.4);
            amplitude *= 0.5;
          }
          return value;
        }

        void main() {
          vec2 centered = vUv - 0.5;
          vec2 wind = normalize(uWind + vec2(0.001, 0.001));
          vec2 drift = uFlow * 0.2 + wind * uTime * 0.008 + vec2(sin(uTime * 0.12) * 0.01, cos(uTime * 0.1) * 0.008);
          float edgeFade = smoothstep(0.0, 0.08, vUv.x) *
                           smoothstep(0.0, 0.08, vUv.y) *
                           smoothstep(0.0, 0.08, 1.0 - vUv.x) *
                           smoothstep(0.0, 0.08, 1.0 - vUv.y);
          float centerLight = smoothstep(0.64, 0.02, distance(vUv, vec2(0.54, 0.48)));
          float radial = smoothstep(0.9, 0.08, length(centered * vec2(1.1, 2.0)));
          float vapor = fbm(vUv * vec2(5.4, 3.0) + drift * 1.6);
          float filamentLine = abs(centered.y * 2.6 + centered.x * 0.28 + sin(uTime * 0.22 + centered.x * 7.0) * 0.04);
          float filament = pow(1.0 - smoothstep(0.02, 0.42, filamentLine), 2.0) * smoothstep(0.58, 0.06, abs(centered.x));
          float alpha = (radial * vapor * 0.18 + filament * 0.22) * edgeFade * uOpacity * (0.82 + uPointerEnergy * 0.12);
          float backlight = clamp(centerLight * 0.68 + filament * 0.42 + vapor * 0.16, 0.0, 1.0);
          vec3 color = mix(uRimColor, uSunColor, backlight);
          color = mix(uShadeColor, color, clamp(0.64 + backlight * 0.36, 0.0, 1.0));
          alpha *= mix(1.0, 0.78, centerLight);
          if (alpha < 0.032) discard;
          gl_FragColor = vec4(color, alpha);
        }
      `,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: theme.isDark ? THREE.NormalBlending : THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    }),
    materials
  );

const makeCloudPlaneMaterial = (
  THREE: typeof THREEType,
  cloudAtlas: THREEType.Texture,
  theme: ThemeColors,
  atlasRect: [number, number, number, number],
  opacity: number,
  radius: number,
  seed: number,
  materials: THREEType.Material[]
) =>
  registerMaterial(
    new THREE.ShaderMaterial({
      uniforms: {
        uCloudMap: { value: cloudAtlas },
        uAtlasRect: { value: new THREE.Vector4(...atlasRect) },
        uLightColor: { value: theme.cloudLight },
        uMidColor: { value: theme.cloudMid },
        uShadeColor: { value: theme.cloudShade },
        uAccentColor: { value: theme.accent2 },
        uSunColor: { value: theme.sunCore },
        uTime: { value: 0 },
        uOpacity: { value: opacity },
        uPointer: { value: new THREE.Vector2(0, 0) },
        uPointerEnergy: { value: 0 },
        uPointerVelocity: { value: new THREE.Vector2(0, 0) },
        uRadius: { value: radius },
        uSunWorld: { value: new THREE.Vector2(0, 0) },
        uSunStrength: { value: theme.isDark ? 0.62 : 0.48 },
        uWind: { value: new THREE.Vector2(0.42, -0.08) },
        uFlow: { value: new THREE.Vector2(0, 0) },
        uCoverage: { value: 0.64 },
        uDetailStrength: { value: 0.28 },
        uWorleyBlend: { value: 0.42 },
        uSeed: { value: seed },
      },
      vertexShader: `
        uniform float uTime;
        varying vec2 vUv;
        varying vec2 vWorld;

        void main() {
          vUv = uv;
          vec3 transformed = position;
          transformed.x += sin(uTime * 0.17 + uv.y * 5.2) * 0.18;
          transformed.y += sin(uTime * 0.31 + uv.x * 2.4) * 0.18;
          vec4 world = modelMatrix * vec4(transformed, 1.0);
          vWorld = world.xy;
          gl_Position = projectionMatrix * viewMatrix * world;
        }
      `,
      fragmentShader: `
        uniform sampler2D uCloudMap;
        uniform vec4 uAtlasRect;
        uniform vec3 uLightColor;
        uniform vec3 uMidColor;
        uniform vec3 uShadeColor;
        uniform vec3 uAccentColor;
        uniform vec3 uSunColor;
        uniform float uTime;
        uniform float uOpacity;
        uniform vec2 uPointer;
        uniform vec2 uPointerVelocity;
        uniform float uPointerEnergy;
        uniform float uRadius;
        uniform vec2 uSunWorld;
        uniform float uSunStrength;
        uniform vec2 uWind;
        uniform vec2 uFlow;
        uniform float uCoverage;
        uniform float uDetailStrength;
        uniform float uWorleyBlend;
        uniform float uSeed;
        varying vec2 vUv;
        varying vec2 vWorld;

        float hash(vec2 p) {
          return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
        }

        float noise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          vec2 u = f * f * (3.0 - 2.0 * f);
          return mix(
            mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
            mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
            u.y
          );
        }

        float hash31(vec3 p) {
          return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453);
        }

        vec3 hash33(vec3 p) {
          p = vec3(
            dot(p, vec3(127.1, 311.7, 74.7)),
            dot(p, vec3(269.5, 183.3, 246.1)),
            dot(p, vec3(113.5, 271.9, 124.6))
          );
          return fract(sin(p) * 43758.5453);
        }

        float noise3D(vec3 p) {
          vec3 i = floor(p);
          vec3 f = fract(p);
          vec3 u = f * f * (3.0 - 2.0 * f);

          float n000 = hash31(i + vec3(0.0, 0.0, 0.0));
          float n100 = hash31(i + vec3(1.0, 0.0, 0.0));
          float n010 = hash31(i + vec3(0.0, 1.0, 0.0));
          float n110 = hash31(i + vec3(1.0, 1.0, 0.0));
          float n001 = hash31(i + vec3(0.0, 0.0, 1.0));
          float n101 = hash31(i + vec3(1.0, 0.0, 1.0));
          float n011 = hash31(i + vec3(0.0, 1.0, 1.0));
          float n111 = hash31(i + vec3(1.0, 1.0, 1.0));

          float nx00 = mix(n000, n100, u.x);
          float nx10 = mix(n010, n110, u.x);
          float nx01 = mix(n001, n101, u.x);
          float nx11 = mix(n011, n111, u.x);
          float nxy0 = mix(nx00, nx10, u.y);
          float nxy1 = mix(nx01, nx11, u.y);
          return mix(nxy0, nxy1, u.z);
        }

        float fbm3D(vec3 p) {
          float sum = 0.0;
          float amp = 0.5;
          float maxAmp = 0.0;
          for (int i = 0; i < 5; i++) {
            sum += noise3D(p) * amp;
            maxAmp += amp;
            p = p * 2.03 + vec3(17.1, 9.2, 5.7);
            amp *= 0.5;
          }
          return sum / maxAmp;
        }

        float worley3D(vec3 p) {
          vec3 i = floor(p);
          vec3 f = fract(p);
          float minDist = 1.0;

          for (int x = -1; x <= 1; x++) {
            for (int y = -1; y <= 1; y++) {
              for (int z = -1; z <= 1; z++) {
                vec3 neighbor = vec3(float(x), float(y), float(z));
                vec3 point = hash33(i + neighbor);
                vec3 diff = neighbor + point - f;
                minDist = min(minDist, dot(diff, diff));
              }
            }
          }
          return sqrt(minDist);
        }

        float remap(float value, float low1, float high1, float low2, float high2) {
          return low2 + clamp((value - low1) / max(high1 - low1, 0.0001), 0.0, 1.0) * (high2 - low2);
        }

        float puff(vec2 p, vec2 center, vec2 radius, float softness) {
          vec2 q = (p - center) / radius;
          float d = dot(q, q);
          return pow(1.0 - smoothstep(0.72, 1.0 + softness, d), 1.35);
        }

        void main() {
          vec2 wind = normalize(uWind + vec2(0.001, 0.001));
          vec2 pointerFlow = uPointerVelocity * 0.0014;
          vec2 wakeVector = normalize(wind * 0.76 + pointerFlow + vec2(0.001, 0.001));
          vec2 wakeNormal = vec2(-wakeVector.y, wakeVector.x);
          float pointerDistance = distance(vWorld, uPointer);
          float pointerWake = smoothstep(uRadius * 0.82, 0.0, pointerDistance) * clamp(uPointerEnergy * 0.48, 0.0, 0.9);
          float trailingWake = smoothstep(uRadius * 0.72, 0.0, distance(vWorld - wakeVector * 16.0, uPointer)) * clamp(uPointerEnergy * 0.34, 0.0, 0.7);
          float localNoise = noise(vUv * 7.0 + uFlow + wind * uTime * 0.018);
          float curlNoise = noise(vUv * 12.0 - uFlow * 0.7 + vec2(uTime * 0.025, -uTime * 0.014)) - 0.5;
          vec2 uvWarp = uFlow * 0.18 +
                        wind * uTime * 0.006 +
                        wind * localNoise * 0.014 +
                        wakeVector * pointerWake * 0.012 +
                        wakeNormal * curlNoise * (0.012 + pointerWake * 0.018) +
                        (localNoise - 0.5) * 0.01;
          vec2 atlasUv = uAtlasRect.xy + clamp(vUv + uvWarp * 0.34, 0.0, 1.0) * uAtlasRect.zw;
          vec4 generatedMap = texture2D(uCloudMap, atlasUv);
          float mapMask = smoothstep(0.04, 0.76, generatedMap.a);
          vec2 cloudUv = (vUv - 0.5) * vec2(2.42, 1.16);
          float proceduralBreakup = fbm3D(vec3(
            cloudUv * vec2(5.4, 3.2) + uFlow * 0.52 + wind * uTime * 0.018,
            uSeed * 2.13 + uTime * 0.052
          ));
          float atlasBreakup = mix(0.88, 1.1, proceduralBreakup);
          vec2 silhouetteUv = cloudUv + vec2(
            fbm3D(vec3(cloudUv * 1.8 + uFlow * 0.8, uSeed + uTime * 0.028)) - 0.5,
            fbm3D(vec3(cloudUv.yx * 1.6 - uFlow * 0.6, uSeed * 1.7 + uTime * 0.022)) - 0.5
          ) * 0.18;
          float edgeFlutter = fbm3D(vec3(silhouetteUv * 4.4 + uFlow * 0.34, uSeed + uTime * 0.032));
          float shapedMask = mapMask + (edgeFlutter - 0.5) * 0.18 * mapMask;
          float coreMask = smoothstep(0.04, 0.72, shapedMask);
          float feather = smoothstep(0.01, 0.42, shapedMask);
          vec3 shapePos = vec3(
            cloudUv * vec2(2.45, 1.55) + uFlow * 0.42 + wind * uTime * 0.012,
            uTime * 0.035
          );
          float perlinShape = fbm3D(shapePos);
          float cellShape = 1.0 - worley3D(shapePos * 3.85 + vec3(0.0, 0.0, uTime * 0.025));
          float largeShape = mix(perlinShape, cellShape, uWorleyBlend);
          largeShape = remap(largeShape, max(0.18, 1.0 - uCoverage), 1.0, 0.0, 1.0);
          float detail = fbm3D(vec3(cloudUv * vec2(8.5, 4.6) - uFlow * 0.9 + wind * uTime * 0.025, uTime * 0.085));
          float erosion = worley3D(vec3(cloudUv * vec2(7.4, 4.2) + uFlow * 0.45, uTime * 0.06));
          float altitudeEnvelope = smoothstep(-0.68, -0.34, cloudUv.y) * smoothstep(0.9, 0.34, cloudUv.y);
          float flatBase = smoothstep(-0.68, -0.36, cloudUv.y);
          float proceduralDensity = max(largeShape * 0.82 + coreMask * 0.72 - detail * uDetailStrength - erosion * 0.08, 0.0);
          float density = clamp(
            (proceduralDensity * feather * 0.74 + mapMask * coreMask * 0.24) *
            altitudeEnvelope * flatBase * atlasBreakup * 1.05,
            0.0,
            1.0
          );
          float edgeFade = smoothstep(0.0, 0.06, vUv.x) *
                           smoothstep(0.0, 0.06, vUv.y) *
                           smoothstep(0.0, 0.06, 1.0 - vUv.x) *
                           smoothstep(0.0, 0.06, 1.0 - vUv.y);
          float verticalLight = smoothstep(0.1, 0.95, 1.0 - vUv.y);
          float sunDistance = distance(vWorld, uSunWorld);
          float sunScatter = exp(-sunDistance * 0.036) * density * uSunStrength;
          float sunProximity = smoothstep(86.0, 4.0, sunDistance);
          float edgeDensity = smoothstep(0.06, 0.34, density) * smoothstep(0.86, 0.38, density);
          float silverLining = edgeDensity * smoothstep(84.0, 6.0, sunDistance) * uSunStrength;
          float backlitVapor = clamp((sunProximity * 0.78 + sunScatter * 0.42 + silverLining * 0.5) * density, 0.0, 1.0);
          float baseShadow = smoothstep(-0.18, -0.58, cloudUv.y) * density * 0.18;
          vec3 color = mix(uShadeColor, uMidColor, clamp(density * 1.28 + largeShape * 0.18, 0.0, 1.0));
          color = mix(color, uLightColor, verticalLight * density * 0.36);
          color = mix(color, uShadeColor, baseShadow);
          color = mix(color, uLightColor, clamp(sunScatter * 0.34 + silverLining * 0.36, 0.0, 0.64));
          color = mix(color, uSunColor, clamp(backlitVapor * 0.72, 0.0, 0.84));
          color += uAccentColor * clamp(sunScatter * 0.1 + silverLining * 0.14 + backlitVapor * 0.18, 0.0, 0.26);
          color = mix(color, uAccentColor, 0.07 + pointerWake * 0.04);
          float wakeThinning = 1.0 - clamp(pointerWake * 0.18 + trailingWake * 0.12, 0.0, 0.28);
          float sunThinning = 1.0 - clamp(sunProximity * density * 0.46 + silverLining * 0.18, 0.0, 0.58);
          float alpha = (pow(mapMask, 1.08) * 0.08 + pow(density, 0.7) * 0.72) *
                        uOpacity * edgeFade * (0.82 + localNoise * 0.12 + sunScatter * 0.06) * wakeThinning * sunThinning;
          if (alpha < 0.032) discard;
          gl_FragColor = vec4(color, alpha);
        }
      `,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: theme.isDark ? THREE.NormalBlending : THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    }),
    materials
  );

const createCloudSystem = (
  THREE: typeof THREEType,
  cloudAtlas: THREEType.Texture,
  theme: ThemeColors,
  geometries: THREEType.BufferGeometry[],
  materials: THREEType.Material[],
  textures: THREEType.Texture[],
  index: number,
  width: number
): CloudSystem => {
  const cloudAnchors = width <= 700
    ? [
        { x: -26, y: 39, z: -90, w: 70, h: 42, opacity: 0.42, radius: 42, rect: [0.02, 0.05, 0.86, 0.34] as [number, number, number, number] },
        { x: 34, y: 48, z: -108, w: 66, h: 39, opacity: 0.36, radius: 42, rect: [0.04, 0.34, 0.88, 0.28] as [number, number, number, number] },
        { x: 0, y: 58, z: -126, w: 78, h: 38, opacity: 0.24, radius: 46, rect: [0.08, 0.6, 0.82, 0.28] as [number, number, number, number] },
      ]
    : [
        { x: -76, y: 39, z: -86, w: 84, h: 50, opacity: 0.38, radius: 54, rect: [0.02, 0.05, 0.86, 0.34] as [number, number, number, number] },
        { x: -18, y: 51, z: -110, w: 76, h: 50, opacity: 0.42, radius: 50, rect: [0.04, 0.34, 0.88, 0.28] as [number, number, number, number] },
        { x: 54, y: 45, z: -96, w: 86, h: 52, opacity: 0.44, radius: 52, rect: [0.06, 0.58, 0.86, 0.3] as [number, number, number, number] },
        { x: 112, y: 57, z: -126, w: 80, h: 44, opacity: 0.26, radius: 54, rect: [0.0, 0.14, 0.98, 0.42] as [number, number, number, number] },
      ];
  const anchor = cloudAnchors[index % cloudAnchors.length];
  const geometry = registerGeometry(new THREE.PlaneGeometry(anchor.w, anchor.h, 42, 16), geometries);
  const proceduralTexture = makeProceduralCloudTexture(THREE, 4201 + index * 911);
  if (proceduralTexture) textures.push(proceduralTexture);
  const material = makeCloudPlaneMaterial(
    THREE,
    proceduralTexture ?? cloudAtlas,
    theme,
    proceduralTexture ? [0, 0, 1, 1] : anchor.rect,
    theme.isDark ? anchor.opacity : anchor.opacity * 0.18,
    anchor.radius,
    13.37 + index * 7.91,
    materials
  );
  const mesh = new THREE.Mesh(geometry, material);
  mesh.renderOrder = 2;
  const group = new THREE.Group();
  group.add(mesh);

  const random = makeSeededRandom(7319 + index * 811);
  const puffGeometry = registerGeometry(new THREE.SphereGeometry(1, 32, 16), geometries);
  const puffMaterial = registerMaterial(
    new THREE.MeshBasicMaterial({
      color: theme.cloudMid,
      transparent: true,
      opacity: theme.isDark ? 0.022 : 0.006,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
    }),
    materials
  );
  for (let i = 0; i < 30; i += 1) {
    const puff = new THREE.Mesh(puffGeometry, puffMaterial);
    const distance = Math.pow(random(), 0.72);
    const angle = random() * Math.PI * 2;
    const x = Math.cos(angle) * anchor.w * 0.25 * distance + (random() - 0.5) * anchor.w * 0.12;
    const y = Math.sin(angle) * anchor.h * 0.18 * distance + (random() - 0.5) * anchor.h * 0.16;
    const z = (random() - 0.5) * 12;
    puff.position.set(x, y, z);
    puff.scale.set(
      mix(anchor.w * 0.06, anchor.w * 0.16, random()),
      mix(anchor.h * 0.08, anchor.h * 0.2, random()),
      mix(3.2, 9.4, random())
    );
    puff.rotation.set(random() * Math.PI, random() * Math.PI, random() * Math.PI);
    puff.renderOrder = 2.3;
    group.add(puff);
  }
  group.position.set(anchor.x, anchor.y, anchor.z);

  return {
    group,
    material,
    baseX: anchor.x,
    baseY: anchor.y,
    depth: anchor.z,
    parallax: 0.72 + index * 0.25,
    drift: 0.13 + index * 0.04,
    radius: anchor.radius,
    windSpeed: 0.085 + index * 0.024,
    windPhase: index * 1.73,
    verticalDrift: 0.34 + index * 0.08,
  };
};

const createSunAndMoon = (
  THREE: typeof THREEType,
  cloudAtlas: THREEType.Texture,
  theme: ThemeColors,
  width: number,
  geometries: THREEType.BufferGeometry[],
  materials: THREEType.Material[],
  textures: THREEType.Texture[]
): CelestialSystem => {
  const sunGroup = new THREE.Group();
  const flareTint = blendColor(THREE, theme.sunCore, theme.sunRim, 0.34);
  const sunGeometry = registerGeometry(new THREE.SphereGeometry(width <= 700 ? 3.4 : 4.8, 48, 32), geometries);
  const sunMaterial = registerMaterial(
    new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uCore: { value: theme.sunCore },
        uRim: { value: theme.sunRim },
      },
      vertexShader: `
        varying vec3 vNormal;
        varying vec3 vWorld;
        void main() {
          vNormal = normalize(normalMatrix * normal);
          vec4 world = modelMatrix * vec4(position, 1.0);
          vWorld = world.xyz;
          gl_Position = projectionMatrix * viewMatrix * world;
        }
      `,
      fragmentShader: `
        uniform float uTime;
        uniform vec3 uCore;
        uniform vec3 uRim;
        varying vec3 vNormal;
        varying vec3 vWorld;
        float plasma(vec3 p) {
          return sin(p.x * 3.1 + uTime * 0.9) * 0.5 +
                 sin(p.y * 4.7 - uTime * 0.7) * 0.35 +
                 sin((p.x + p.y + p.z) * 2.2 + uTime * 0.45) * 0.25;
        }
        void main() {
          float fresnel = pow(1.0 - abs(vNormal.z), 2.2);
          float heat = plasma(normalize(vWorld) * 1.4);
          vec3 color = mix(uCore, uRim, clamp(fresnel + heat * 0.16, 0.0, 1.0));
          gl_FragColor = vec4(color, 1.0);
        }
      `,
    }),
    materials
  );
  const sun = new THREE.Mesh(sunGeometry, sunMaterial);
  sun.renderOrder = 5;
  sunGroup.add(sun);

  const haloTexture = makeRadialTexture(THREE, [
    [0, colorStyle(theme.sunCore, 0.86)],
    [0.18, colorStyle(theme.sunRim, 0.32)],
    [0.56, colorStyle(theme.accent2, 0.12)],
    [1, colorStyle(theme.sunRim, 0)],
  ], 384);
  if (haloTexture) textures.push(haloTexture);
  const haloMaterial = registerMaterial(
    new THREE.SpriteMaterial({
      map: haloTexture ?? undefined,
      transparent: true,
      opacity: theme.isDark ? 0.72 : 0.52,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
    }),
    materials
  );
  const halo = new THREE.Sprite(haloMaterial);
  halo.scale.set(width <= 700 ? 21 : 38, width <= 700 ? 21 : 38, 1);
  halo.renderOrder = 4;
  sunGroup.add(halo);

  const atmosphereTexture = makeRadialTexture(THREE, [
    [0, colorStyle(theme.sunCore, 0.18)],
    [0.24, colorStyle(theme.sunRim, 0.16)],
    [0.58, colorStyle(theme.accent2, 0.07)],
    [1, colorStyle(theme.sunRim, 0)],
  ], 512);
  if (atmosphereTexture) textures.push(atmosphereTexture);
  const atmosphereMaterial = registerMaterial(
    new THREE.SpriteMaterial({
      map: atmosphereTexture ?? undefined,
      transparent: true,
      opacity: theme.isDark ? 0.36 : 0.28,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
    }),
    materials
  );
  const atmosphere = new THREE.Sprite(atmosphereMaterial);
  atmosphere.scale.set(width <= 700 ? 46 : 88, width <= 700 ? 32 : 62, 1);
  atmosphere.renderOrder = 3;
  sunGroup.add(atmosphere);

  const flareStreaks: THREEType.Sprite[] = [];
  const streakTexture = makeLinearFlareTexture(THREE, theme, 640);
  if (streakTexture) textures.push(streakTexture);
  [
    { scaleX: width <= 700 ? 92 : 210, scaleY: width <= 700 ? 3.2 : 5.8, opacity: theme.isDark ? 0.62 : 0.42, rotation: -0.035 },
    { scaleX: width <= 700 ? 58 : 132, scaleY: width <= 700 ? 1.6 : 2.6, opacity: theme.isDark ? 0.34 : 0.22, rotation: 0.18 },
  ].forEach((streak) => {
    const material = registerMaterial(
      new THREE.SpriteMaterial({
        map: streakTexture ?? undefined,
        transparent: true,
        opacity: streak.opacity,
        rotation: streak.rotation,
        depthWrite: false,
        depthTest: false,
        blending: THREE.AdditiveBlending,
      }),
      materials
    );
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(streak.scaleX, streak.scaleY, 1);
    sprite.renderOrder = 7;
    sprite.userData.baseOpacity = streak.opacity;
    sunGroup.add(sprite);
    flareStreaks.push(sprite);
  });

  if (streakTexture) {
    const flarePlaneMaterial = registerMaterial(
      new THREE.MeshBasicMaterial({
        map: streakTexture,
        transparent: true,
        opacity: theme.isDark ? 0.5 : 0.34,
        depthWrite: false,
        depthTest: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
      }),
      materials
    );
    const flarePlaneGeometry = registerGeometry(
      new THREE.PlaneGeometry(width <= 700 ? 96 : 224, width <= 700 ? 10 : 18, 1, 1),
      geometries
    );
    const flarePlane = new THREE.Mesh(flarePlaneGeometry, flarePlaneMaterial);
    flarePlane.rotation.z = -0.035;
    flarePlane.position.z = 3;
    flarePlane.renderOrder = 10;
    sunGroup.add(flarePlane);
  }

  const mistMaterial = makeSunMistMaterial(THREE, theme, materials);
  const mistGeometry = registerGeometry(new THREE.PlaneGeometry(width <= 700 ? 86 : 152, width <= 700 ? 50 : 76, 1, 1), geometries);
  const mist = new THREE.Mesh(mistGeometry, mistMaterial);
  mist.position.set(width <= 700 ? -7 : -18, width <= 700 ? -8 : -9, -2.5);
  mist.renderOrder = 3.5;
  sunGroup.add(mist);

  const veilMaterial = makeSunVeilMaterial(THREE, cloudAtlas, theme, materials);
  const veilGeometry = registerGeometry(new THREE.PlaneGeometry(width <= 700 ? 64 : 108, width <= 700 ? 25 : 34, 24, 8), geometries);
  const veil = new THREE.Mesh(veilGeometry, veilMaterial);
  veil.position.set(width <= 700 ? -10 : -17, width <= 700 ? -1 : -2, 1.5);
  veil.rotation.z = width <= 700 ? -0.04 : -0.025;
  veil.renderOrder = 8;
  sunGroup.add(veil);

  sunGroup.position.set(width <= 700 ? 20 : 52, width <= 700 ? 48 : 43, -56);

  const flareGhostGroup = new THREE.Group();
  const flareGhosts: THREEType.Sprite[] = [];
  const ghostCore = makeRadialTexture(THREE, [
    [0, colorStyle(theme.sunCore, 0.5)],
    [0.28, colorStyle(theme.sunRim, 0.14)],
    [0.46, colorStyle(flareTint, 0.06)],
    [0.62, colorStyle(theme.sunCore, 0.14)],
    [0.66, colorStyle(theme.sunRim, 0.04)],
    [1, colorStyle(theme.sunRim, 0)],
  ], 256);
  const ghostRing = makeRadialTexture(THREE, [
    [0, colorStyle(flareTint, 0)],
    [0.42, colorStyle(flareTint, 0.02)],
    [0.49, colorStyle(theme.sunCore, 0.28)],
    [0.57, colorStyle(theme.sunRim, 0.04)],
    [1, colorStyle(theme.sunRim, 0)],
  ], 256);
  if (ghostCore) textures.push(ghostCore);
  if (ghostRing) textures.push(ghostRing);
  [
    { factor: 0.28, size: width <= 700 ? 8 : 18, opacity: theme.isDark ? 0.34 : 0, texture: ghostCore, z: -64, depthScale: 0.82 },
    { factor: 0.58, size: width <= 700 ? 12 : 26, opacity: theme.isDark ? 0.3 : 0, texture: ghostRing, z: -42, depthScale: 1 },
    { factor: 0.9, size: width <= 700 ? 18 : 38, opacity: theme.isDark ? 0.28 : 0, texture: ghostCore, z: -18, depthScale: 1.26 },
    { factor: 1.2, size: width <= 700 ? 30 : 66, opacity: theme.isDark ? 0.48 : 0, texture: ghostRing, z: 10, depthScale: 1.6 },
  ].forEach((ghost) => {
    const material = registerMaterial(
      new THREE.SpriteMaterial({
        map: ghost.texture ?? undefined,
        transparent: true,
        opacity: ghost.opacity,
        depthWrite: false,
        depthTest: false,
        blending: THREE.AdditiveBlending,
      }),
      materials
    );
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(ghost.size, ghost.size, 1);
    sprite.position.z = ghost.z;
    sprite.renderOrder = 9;
    sprite.userData.factor = ghost.factor;
    sprite.userData.baseSize = ghost.size;
    sprite.userData.baseOpacity = ghost.opacity;
    sprite.userData.targetZ = ghost.z;
    sprite.userData.depthScale = ghost.depthScale;
    flareGhostGroup.add(sprite);
    flareGhosts.push(sprite);
  });

  const moonTexture = makeMoonTexture(THREE, theme);
  if (moonTexture) textures.push(moonTexture);
  const moonMaterial = registerMaterial(
    new THREE.MeshStandardMaterial({
      map: moonTexture ?? undefined,
      color: theme.moonLight,
      roughness: 1,
      metalness: 0,
      transparent: true,
      opacity: theme.isDark ? 0.78 : 0.48,
    }),
    materials
  );
  const moonGeometry = registerGeometry(new THREE.SphereGeometry(width <= 700 ? 3.8 : 5.1, 42, 24), geometries);
  const moon = new THREE.Mesh(moonGeometry, moonMaterial);
  moon.position.set(width <= 700 ? -23 : -58, width <= 700 ? 54 : 48, -84);

  return {
    sunGroup,
    sunMaterial,
    moon,
    flareGhostGroup,
    flareGhosts,
    flareStreaks,
    mistMaterial,
    veilMaterial,
  };
};

export function CinematicSky({ className, sx, ...props }: CinematicSkyProps) {
  const { variants: themeVariants, selectedThemeId } = useArtistTheme();
  const activeThemeStyle = useMemo(
    () =>
      themeVariants.find((theme) => theme.id === selectedThemeId)?.style ||
      themeVariants[0]?.style,
    [selectedThemeId, themeVariants]
  );
  const themeSignature = useMemo(() => {
    if (!activeThemeStyle) return 'css-root';
    return [
      selectedThemeId,
      activeThemeStyle['--backgroundColor'],
      activeThemeStyle['--logoColor'],
      activeThemeStyle['--logoColor2'],
      activeThemeStyle['--textColor'],
    ].join('|');
  }, [activeThemeStyle, selectedThemeId]);
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<SceneHandle | null>(null);
  const initializedRef = useRef(false);
  const sceneGenerationRef = useRef(0);
  const cameraOffsetRef = useRef({ x: 0, y: 0 });
  const targetOffsetRef = useRef({ x: 0, y: 0 });
  const pointerRef = useRef<PointerState>({
    x: 0,
    y: 24,
    velocityX: 0,
    velocityY: 0,
    energy: 0,
    lastX: 0,
    lastY: 0,
    lastTime: 0,
  });

  const initScene = useCallback(async (generation: number) => {
    const container = containerRef.current;
    if (!container || initializedRef.current) return;
    initializedRef.current = true;

    const [
      THREE,
      { EffectComposer },
      { RenderPass },
      { UnrealBloomPass },
      { ShaderPass },
      lensflareModule,
    ] = await Promise.all([
      import('three'),
      import('three/examples/jsm/postprocessing/EffectComposer.js'),
      import('three/examples/jsm/postprocessing/RenderPass.js'),
      import('three/examples/jsm/postprocessing/UnrealBloomPass.js'),
      import('three/examples/jsm/postprocessing/ShaderPass.js'),
      import('three/examples/jsm/objects/Lensflare.js').catch(() => null),
    ]);

    if (sceneGenerationRef.current !== generation || !container.isConnected) {
      initializedRef.current = false;
      return;
    }

    const cloudAtlas = await loadCloudAtlas(THREE);

    if (sceneGenerationRef.current !== generation || !container.isConnected) {
      cloudAtlas.dispose();
      initializedRef.current = false;
      return;
    }

    const rect = container.getBoundingClientRect();
    const width = rect.width || window.innerWidth;
    const height = rect.height || window.innerHeight;
    const pixelRatio = Math.min(window.devicePixelRatio || 1, width <= 700 ? 1.35 : 1.75);
    const theme = getThemeColors(THREE, activeThemeStyle);
    const geometries: THREEType.BufferGeometry[] = [];
    const materials: THREEType.Material[] = [];
    const textures: THREEType.Texture[] = [];
    const disposables: Array<{ dispose: () => void }> = [];

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(48, width / height, 0.1, 600);
    camera.position.set(0, 0, 95);

    let renderer: THREEType.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({
        alpha: true,
        antialias: true,
        powerPreference: 'high-performance',
        premultipliedAlpha: false,
      });
    } catch (error) {
      console.warn('[CinematicSky] WebGL unavailable, skipping cinematic sky', error);
      initializedRef.current = false;
      return;
    }

    renderer.setPixelRatio(pixelRatio);
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = theme.isDark ? 1.08 : 0.96;
    renderer.domElement.setAttribute('aria-hidden', 'true');
    container.appendChild(renderer.domElement);

    textures.push(cloudAtlas);

    const ambientLight = new THREE.AmbientLight(0xffffff, theme.isDark ? 0.36 : 0.52);
    const keyLight = new THREE.DirectionalLight(theme.sunCore, 1.1);
    keyLight.position.set(36, 44, 54);
    scene.add(ambientLight, keyLight);

    const composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(width, height),
      theme.isDark ? 0.44 : 0.32,
      0.68,
      0.34
    );
    composer.addPass(bloomPass);
    const atmospherePass = makeAtmospherePass(THREE, ShaderPass, theme);
    composer.addPass(atmospherePass);

    const clouds = (width <= 700 ? [0, 1, 2] : [0, 1, 2, 3]).map((index) => {
      const cloud = createCloudSystem(THREE, cloudAtlas, theme, geometries, materials, textures, index, width);
      scene.add(cloud.group);
      return cloud;
    });

    const celestial = createSunAndMoon(THREE, cloudAtlas, theme, width, geometries, materials, textures);
    const { sunGroup, sunMaterial, moon, flareGhostGroup, flareGhosts, flareStreaks, mistMaterial, veilMaterial } = celestial;
    scene.add(sunGroup, moon, flareGhostGroup);

    if (lensflareModule && theme.isDark) {
      const flareTint = blendColor(THREE, theme.sunCore, theme.sunRim, 0.34);
      const { Lensflare, LensflareElement } = lensflareModule;
      const flareCore = makeRadialTexture(THREE, [
        [0, colorStyle(theme.sunCore, 0.9)],
        [0.18, colorStyle(theme.sunRim, 0.42)],
        [0.62, colorStyle(flareTint, 0.12)],
        [1, colorStyle(theme.sunRim, 0)],
      ], 256);
      const flareRing = makeRadialTexture(THREE, [
        [0, colorStyle(flareTint, 0)],
        [0.46, colorStyle(flareTint, 0.08)],
        [0.5, colorStyle(theme.sunCore, 0.32)],
        [0.56, colorStyle(theme.sunRim, 0.08)],
        [1, colorStyle(flareTint, 0)],
      ], 256);
      if (flareCore) textures.push(flareCore);
      if (flareRing) textures.push(flareRing);
      if (flareCore && flareRing) {
        const light = new THREE.PointLight(theme.sunCore, theme.isDark ? 1.5 : 1.1, 260, 1.8);
        const lensflare = new Lensflare();
        lensflare.addElement(new LensflareElement(flareCore, width <= 700 ? 104 : 240, 0, theme.sunCore));
        lensflare.addElement(new LensflareElement(flareRing, width <= 700 ? 48 : 108, 0.14, flareTint));
        lensflare.addElement(new LensflareElement(flareCore, width <= 700 ? 20 : 48, 0.32, theme.sunRim));
        lensflare.addElement(new LensflareElement(flareRing, width <= 700 ? 42 : 94, 0.58, theme.sunCore));
        lensflare.addElement(new LensflareElement(flareCore, width <= 700 ? 34 : 82, 0.86, flareTint));
        light.add(lensflare);
        sunGroup.add(light);
        if ('dispose' in lensflare && typeof lensflare.dispose === 'function') {
          disposables.push(lensflare);
        }
      }
    }

    const handleInput = (clientX: number, clientY: number) => {
      const inputRect = container.getBoundingClientRect();
      const normalizedX = clamp((clientX - (inputRect.left + inputRect.width / 2)) / (inputRect.width / 2), -1, 1);
      const normalizedY = clamp((clientY - (inputRect.top + inputRect.height / 2)) / (inputRect.height / 2), -1, 1);
      const now = performance.now();
      const previous = pointerRef.current;
      const deltaTime = Math.max(16, now - (previous.lastTime || now - 16));
      const velocityX = (normalizedX - previous.lastX) / deltaTime;
      const velocityY = (normalizedY - previous.lastY) / deltaTime;
      const speed = Math.hypot(velocityX, velocityY) * 1000;

      targetOffsetRef.current = { x: normalizedX, y: normalizedY };
      pointerRef.current = {
        x: normalizedX * 96,
        y: -normalizedY * 58 + 24,
        velocityX: velocityX * 1600,
        velocityY: -velocityY * 1200,
        energy: clamp(previous.energy + speed * 0.2 + 0.1, 0, 1.8),
        lastX: normalizedX,
        lastY: normalizedY,
        lastTime: now,
      };
    };

    const handleMouseMove = (event: MouseEvent) => handleInput(event.clientX, event.clientY);
    const handleTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (touch) handleInput(touch.clientX, touch.clientY);
    };
    const handleMouseLeave = () => {
      targetOffsetRef.current = { x: 0, y: 0 };
    };
    const handleDeviceOrientation = (event: DeviceOrientationEvent) => {
      const beta = event.beta ?? 0;
      const gamma = event.gamma ?? 0;
      targetOffsetRef.current = {
        x: clamp(gamma / 30, -1, 1),
        y: clamp((beta - 45) / 30, -1, 1),
      };
    };

    let animationId = 0;
    let lastFrame = 0;
    const targetFPS = width <= 700 ? 24 : 30;
    const frameInterval = 1000 / targetFPS;
    const baseCamera = new THREE.Vector3(0, 0, 95);
    const lookTarget = new THREE.Vector3(0, 0, -78);

    const render = (time = 0) => {
      animationId = requestAnimationFrame(render);
      const delta = time - lastFrame;
      if (delta < frameInterval) return;
      lastFrame = time - (delta % frameInterval);

      const seconds = time * 0.001;
      cameraOffsetRef.current.x += (targetOffsetRef.current.x - cameraOffsetRef.current.x) * 0.15;
      cameraOffsetRef.current.y += (targetOffsetRef.current.y - cameraOffsetRef.current.y) * 0.15;
      const { x, y } = cameraOffsetRef.current;
      const pointer = pointerRef.current;
      pointer.energy *= 0.9;
      pointer.velocityX *= 0.88;
      pointer.velocityY *= 0.88;

      camera.position.x = baseCamera.x + x * 13;
      camera.position.y = baseCamera.y + y * 8;
      camera.lookAt(lookTarget.x + x * 4.2, lookTarget.y + y * 2.4, lookTarget.z);

      const sunX = (width <= 700 ? 20 : 52) + x * -13;
      const sunY = (width <= 700 ? 48 : 43) + y * -5;

      clouds.forEach((cloud, cloudIndex) => {
        const windX = 0.48 + cloudIndex * 0.055;
        const windY = -0.08 + Math.sin(cloud.windPhase) * 0.035;
        const driftX =
          Math.sin(seconds * cloud.windSpeed + cloud.windPhase) * (2.1 + cloudIndex * 0.25) +
          Math.sin(seconds * cloud.windSpeed * 0.37 + cloud.windPhase * 1.6) * 0.9;
        const driftY =
          Math.sin(seconds * cloud.verticalDrift * 0.22 + cloud.windPhase) * 0.46 +
          Math.cos(seconds * cloud.windSpeed * 0.41 + cloud.windPhase) * 0.22;
        const flowX = seconds * cloud.windSpeed * 0.028 + Math.sin(seconds * 0.045 + cloud.windPhase) * 0.026;
        const flowY = seconds * windY * 0.012 + Math.cos(seconds * 0.038 + cloud.windPhase) * 0.018;
        cloud.group.position.x = cloud.baseX + x * cloud.parallax * -6 + driftX;
        cloud.group.position.y = cloud.baseY + y * cloud.parallax * -3 + driftY;
        cloud.group.position.z = cloud.depth;
        cloud.material.uniforms.uTime.value = seconds + cloudIndex * 0.7;
        cloud.material.uniforms.uPointer.value.set(pointer.x, pointer.y);
        cloud.material.uniforms.uPointerEnergy.value = pointer.energy;
        cloud.material.uniforms.uPointerVelocity.value.set(pointer.velocityX, pointer.velocityY);
        cloud.material.uniforms.uSunWorld.value.set(sunX, sunY);
        cloud.material.uniforms.uWind.value.set(windX, windY);
        cloud.material.uniforms.uFlow.value.set(flowX, flowY);
      });

      sunGroup.position.x = sunX;
      sunGroup.position.y = sunY;
      sunGroup.rotation.y = seconds * 0.08;
      (sunMaterial as THREEType.ShaderMaterial).uniforms.uTime.value = seconds;
      mistMaterial.uniforms.uTime.value = seconds;
      mistMaterial.uniforms.uPointerEnergy.value = pointer.energy;
      mistMaterial.uniforms.uWind.value.set(0.42 + x * 0.12, -0.06 + y * 0.04);
      mistMaterial.uniforms.uFlow.value.set(seconds * 0.018 + Math.sin(seconds * 0.08) * 0.04, Math.cos(seconds * 0.06) * 0.026);
      veilMaterial.uniforms.uTime.value = seconds;
      veilMaterial.uniforms.uPointerEnergy.value = pointer.energy;
      veilMaterial.uniforms.uWind.value.set(0.46 + x * 0.1, -0.05 + y * 0.03);
      veilMaterial.uniforms.uFlow.value.set(seconds * 0.014 + Math.sin(seconds * 0.07) * 0.03, Math.cos(seconds * 0.05) * 0.02);

      flareStreaks.forEach((streak, index) => {
        const material = streak.material as THREEType.SpriteMaterial;
        const baseOpacity = Number(streak.userData.baseOpacity ?? 0.2);
        const pulse = 0.9 + Math.sin(seconds * (1.2 + index * 0.4)) * 0.12 + pointer.energy * 0.12;
        material.opacity = clamp(baseOpacity * pulse, 0, baseOpacity * 1.55);
        streak.scale.x += ((width <= 700 ? 48 - index * 17 : 118 - index * 44) * (1 + Math.abs(x) * 0.12) - streak.scale.x) * 0.08;
      });

      flareGhosts.forEach((ghost, index) => {
        const factor = Number(ghost.userData.factor ?? 0.5);
        const baseSize = Number(ghost.userData.baseSize ?? 8);
        const baseOpacity = Number(ghost.userData.baseOpacity ?? 0.12);
        const targetZ = Number(ghost.userData.targetZ ?? ghost.position.z);
        const depthScale = Number(ghost.userData.depthScale ?? 1);
        const travelX = -sunX * factor + x * (14 + index * 2.4);
        const travelY = -sunY * factor * 0.42 + y * (8 + index) + Math.sin(seconds * 0.7 + index) * 0.6;
        ghost.position.x += (travelX - ghost.position.x) * 0.12;
        ghost.position.y += (travelY - ghost.position.y) * 0.12;
        ghost.position.z += (targetZ - ghost.position.z) * 0.08;
        ghost.scale.setScalar(baseSize * depthScale * (0.94 + Math.sin(seconds * 0.9 + index) * 0.06 + pointer.energy * 0.06));
        (ghost.material as THREEType.SpriteMaterial).opacity = clamp(
          baseOpacity * (1.08 + Math.abs(x) * 0.82 + pointer.energy * 0.2),
          0,
          baseOpacity * 2.1
        );
      });

      moon.position.x = (width <= 700 ? -23 : -58) + x * -5;
      moon.position.y = (width <= 700 ? 54 : 48) + y * -2.8;
      moon.rotation.y = seconds * 0.05;

      if (bloomPass) {
        bloomPass.strength = (theme.isDark ? 0.44 : 0.32) + pointer.energy * 0.08;
      }
      if (atmospherePass) {
        atmospherePass.uniforms.uTime.value = seconds;
        atmospherePass.uniforms.uPointer.value.set(x, y);
      }

      composer.render();
    };

    const handleResize = () => {
      const newRect = container.getBoundingClientRect();
      const newWidth = newRect.width || window.innerWidth;
      const newHeight = newRect.height || window.innerHeight;
      const nextPixelRatio = Math.min(window.devicePixelRatio || 1, newWidth <= 700 ? 1.35 : 1.75);
      camera.aspect = newWidth / newHeight;
      camera.updateProjectionMatrix();
      renderer.setPixelRatio(nextPixelRatio);
      renderer.setSize(newWidth, newHeight);
      composer.setSize(newWidth, newHeight);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('deviceorientation', handleDeviceOrientation);
    container.addEventListener('mouseleave', handleMouseLeave);
    animationId = requestAnimationFrame(render);

    sceneRef.current = {
      scene,
      camera,
      renderer,
      composer,
      bloomPass,
      atmospherePass,
      animationId,
      handleResize,
      removeInputListeners: () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('deviceorientation', handleDeviceOrientation);
        container.removeEventListener('mouseleave', handleMouseLeave);
      },
      geometries,
      materials,
      textures,
      disposables,
    };
  }, [activeThemeStyle, themeSignature]);

  useEffect(() => {
    const generation = sceneGenerationRef.current + 1;
    sceneGenerationRef.current = generation;

    initScene(generation).catch((error) => {
      console.warn('[CinematicSky] Three.js sky init failed', error);
      initializedRef.current = false;
    });

    return () => {
      sceneGenerationRef.current += 1;
      if (!sceneRef.current) {
        initializedRef.current = false;
        return;
      }
      cancelAnimationFrame(sceneRef.current.animationId);
      window.removeEventListener('resize', sceneRef.current.handleResize);
      sceneRef.current.removeInputListeners();
      sceneRef.current.disposables.forEach((item) => item.dispose());
      sceneRef.current.geometries.forEach((geometry) => geometry.dispose());
      sceneRef.current.materials.forEach((material) => material.dispose());
      sceneRef.current.textures.forEach((texture) => texture.dispose());
      sceneRef.current.composer.dispose();
      sceneRef.current.renderer.dispose();
      const canvas = sceneRef.current.renderer.domElement;
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
      sceneRef.current = null;
      initializedRef.current = false;
    };
  }, [initScene]);

  return (
    <Box
      ref={containerRef}
      className={className}
      aria-hidden="true"
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 0,
        background: 'transparent',
        mixBlendMode: 'screen',
        '& canvas': {
          display: 'block',
          width: '100%',
          height: '100%',
        },
        ...sx,
      }}
      {...props}
    />
  );
}

export default CinematicSky;
